import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Church, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { loginUser, persistToken } from "@/lib/api";
import { toast } from "sonner";
import { getLiturgicalSeason, getSeasonLogo } from "@/lib/liturgicalCalendar";
import PublicNavigation from "@/components/PublicNavigation";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [staySignedIn, setStaySignedIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [season] = useState(() => getLiturgicalSeason());

  // Auto-redirect if already authenticated
  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token") || (() => {
      const m = document.cookie.match(/(?:^|; )parish_token=([^;]*)/);
      return m ? m[1] : null;
    })();
    if (token) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Use XMLHttpRequest to avoid fetch body stream issues
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${apiUrl}/api/auth/login`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        
        xhr.onload = function() {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(response);
            } else {
              reject(new Error(response.detail || 'Login failed'));
            }
          } catch (parseError) {
            reject(new Error('Invalid server response'));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error - please check your connection'));
        };
        
        xhr.ontimeout = function() {
          reject(new Error('Request timed out'));
        };
        
        xhr.timeout = 30000; // 30 second timeout
        xhr.send(JSON.stringify({ email, password }));
      });
      
      // Store token based on "stay signed in" preference
      try {
        persistToken(data.token, data.user, staySignedIn);
      } catch (storageError) {
        console.warn("Storage error, trying fallback:", storageError);
        try {
          persistToken(data.token, data.user, true);
        } catch {
          persistToken(data.token, data.user, false);
        }
      }
      
      // Set flag to trigger beta notice on first dashboard visit after login
      sessionStorage.setItem('parish_just_logged_in', 'true');
      
      toast.success("Welcome back!");
      navigate("/dashboard", { state: { user: data.user } });
    } catch (err) {
      console.error("Login error:", err);
      const errorMessage = err.message || "Invalid credentials";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      <PublicNavigation />
      
      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center justify-center gap-3 mb-10">
            <img 
              src={getSeasonLogo(season)} 
              alt="Parish" 
              className="w-11 h-11 rounded-lg transition-all duration-500"
            />
            <span className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26] tracking-tight">Parish</span>
          </div>

          <Card className="bg-white border-[#E5E0D5] shadow-lg" data-testid="login-card">
          <CardHeader className="pb-4">
            <CardTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26]">Welcome Back</CardTitle>
            <CardDescription className="font-['Nunito'] text-[#6B665E]">Sign in to your parish community</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Button
              variant="outline"
              className="w-full h-11 border-[#E5E0D5] text-[#2D2A26] hover:bg-[#F0EBE0] font-['Nunito'] flex items-center gap-3"
              onClick={handleGoogleLogin}
              data-testid="login-google-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-[#E5E0D5]" /></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-3 text-[#6B665E] font-['Nunito']">or sign in with email</span></div>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label className="font-['Nunito'] text-[#2D2A26] text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-[#6B665E]" />
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 border-[#E5E0D5] focus-visible:ring-[var(--liturgical-primary)] font-['Nunito']"
                    data-testid="login-email-input"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-['Nunito'] text-[#2D2A26] text-sm">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-[#6B665E]" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 border-[#E5E0D5] font-['Nunito']"
                    style={{ '--tw-ring-color': season.colors.primary }}
                    data-testid="login-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-[#6B665E] hover:text-[#2D2A26] transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="stay-signed-in" 
                    checked={staySignedIn}
                    onCheckedChange={setStaySignedIn}
                    className="border-[#E5E0D5] data-[state=checked]:bg-[var(--liturgical-primary)] data-[state=checked]:border-[var(--liturgical-primary)]"
                    data-testid="stay-signed-in-checkbox"
                  />
                  <label 
                    htmlFor="stay-signed-in" 
                    className="text-sm font-['Nunito'] text-[#6B665E] cursor-pointer select-none"
                  >
                    Stay signed in
                  </label>
                </div>
                <Link 
                  to="/forgot-password" 
                  className="text-sm font-['Nunito'] hover:underline"
                  style={{ color: season.colors.primary }}
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              
              <Button
                type="submit"
                className="w-full text-white font-['Nunito'] font-semibold h-11 active:scale-95 transition-all"
                style={{ backgroundColor: season.colors.primary }}
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <p className="text-center text-sm text-[#6B665E] font-['Nunito']">
              New to Parish?{" "}
              <Link 
                to="/register" 
                className="font-semibold hover:underline" 
                style={{ color: season.colors.primary }}
                data-testid="login-register-link"
              >
                Create an account
              </Link>
            </p>
          </CardContent>
        </Card>
        
        {/* Legal Links */}
        <div className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-[#6B665E] font-['Nunito']">
          <Link to="/privacy" className="hover:text-[#2D2A26] transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-[#2D2A26] transition-colors">Terms</Link>
          <Link to="/cookies" className="hover:text-[#2D2A26] transition-colors">Cookies</Link>
        </div>
        </div>
      </div>
    </div>
  );
}
